import { CircularProgress, Stack, TextField, Typography } from "@mui/material";

import { NumberInput } from "components/NumberInput";
import { useRoleContext } from "hooks";
import { getUnitOfMeasureLabel } from "utils";

import { DeliveryFileUploadDropzone } from "./components/DeliveryFileUploadDropzone";
import { UNIT_OF_MEASURE_QUANTITY_DECIMAL_PLACES } from "../../constants";

interface Props {
  notes: string;
  onNotesUpdate: (newValue: string) => void;
  loading: boolean;
  error: string | null;
  deliveryTimestamp: string;
  onDeliveryTimestampUpdate: (newValue: string) => void;
  quotingUnitOfMeasure: UnitOfMeasureType;
  unitOfMeasureQuantity: number | null;
  onUnitOfMeasureQuantityUpdate: (newValue: number | null) => void;
  files: File[];
  onFilesChange: (files: File[]) => void;
}

export function MarkShipmentAsDeliveredModalContent(props: Props) {
  const {
    loading,
    notes,
    onNotesUpdate,
    error,
    deliveryTimestamp,
    onDeliveryTimestampUpdate,
    quotingUnitOfMeasure,
    unitOfMeasureQuantity,
    onUnitOfMeasureQuantityUpdate,
    files,
    onFilesChange,
  } = props;
  const { role } = useRoleContext();

  if (loading || !role) {
    return (
      <Stack alignItems="center">
        <CircularProgress />
      </Stack>
    );
  }
  return (
    <Stack direction="column" spacing={2}>
      <Typography>
        Marking a shipment as delivered will update the shipment status. This
        action can not be reversed.
      </Typography>
      <Typography variant="h6" sx={{ mt: ".5rem" }}>
        When was the shipment delivered?
      </Typography>
      <TextField
        id="datetime-local"
        label="Delivery date & time"
        type="datetime-local"
        value={deliveryTimestamp}
        onChange={(e) => onDeliveryTimestampUpdate(e.target.value)}
      />
      {quotingUnitOfMeasure !== "ALL_IN" && (
        <Stack direction="column" spacing={0.5}>
          <Typography variant="h6">Delivered quantity</Typography>
          <NumberInput
            /*
            No max: NumberInput's underlying base-ui NumberField clamps a typed
            value into the range rather than keeping it, which would silently
            record a quantity the shipment was not delivered at and make the
            message below unreachable. maxDecimals only trims to the precision
            the API stores anyway.
            */
            inputLabel={`Quantity (${getUnitOfMeasureLabel(quotingUnitOfMeasure)})`}
            value={unitOfMeasureQuantity}
            min={0}
            maxDecimals={UNIT_OF_MEASURE_QUANTITY_DECIMAL_PLACES}
            onChange={onUnitOfMeasureQuantityUpdate}
          />
          <Typography variant="body2" color="text.secondary">
            {`Enter the actual delivered quantity in ${getUnitOfMeasureLabel(quotingUnitOfMeasure)}.`}
          </Typography>
        </Stack>
      )}
      <Stack direction="column" spacing={0.5}>
        <TextField
          id="delivery"
          variant="outlined"
          onChange={(e) => onNotesUpdate(e.target.value)}
          value={notes}
          sx={{ width: "100%" }}
          multiline
          maxRows={20}
          minRows={1}
          placeholder="Example: Delivered without issues."
          label="Optional additional notes"
          autoFocus
        />
        <Typography variant="body2" color="text.secondary">
          Notes and delivery time will be shared with the{" "}
          {role.type === "CARRIER" ? "shipper" : "carrier"}.
        </Typography>
      </Stack>

      <Stack direction="column" spacing={1}>
        <Typography variant="h6">Attachments</Typography>
        <Typography variant="body2" color="text.secondary">
          Optionally attach files to this delivery. Visible to both the shipper
          and carrier.
        </Typography>
        <DeliveryFileUploadDropzone
          files={files}
          onFilesChange={onFilesChange}
        />
      </Stack>

      {error != null && !loading && (
        <Typography color="red" align="center">
          {error}
        </Typography>
      )}
    </Stack>
  );
}
